import React from 'react';
import { Link } from 'react-router-dom';

const PEOPLE = [
  {
    name: 'Mohammad Fowzan',
    role: 'Founder',
    bio: 'A computer science undergraduate with specialization in AI, machine learning, data science, and deep learning. Passionate about applying these technologies to transform the mental health industry through ethical, data-driven, and scalable solutions.',
  },
  {
    name: 'Syed Mohtashim',
    role: 'Founder',
    bio: 'A computer science postgraduate driven by a deep interest in AI in mental health and its real-world challenges. With a strong research foundation and focus on shaping thoughtful, evidence-based solutions that make mental health support more accessible and human-centered.',
  },
];

export default function Company() {
  return (
    <>
      {/* Hero */}
      <section className="px-6 pt-12 pb-16 md:pt-20 md:pb-24 background-grid">
        <div className="max-w-4xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Company</span>
          <h1 className="text-4xl md:text-6xl font-light mt-4 mb-6 leading-tight">
            We treat mental health as a systems-level problem.
          </h1>
          <div className="w-24 h-px bg-gray-900 mb-8" />
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">
            Cognitics is an AI research organization. We exist to apply the same
            level of technological rigor to mental health that has reshaped finance,
            biology, and infrastructure.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 md:py-32 px-6" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Our Purpose</span>
            <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6">Vision & Mission</h2>
            <div className="w-20 h-px bg-gray-900 mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white/50 backdrop-blur-sm border border-gray-200 p-10 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-6 space-x-4">
                <div className="w-10 h-10 border border-gray-400 flex items-center justify-center text-sm font-light">01</div>
                <h3 className="text-2xl font-light">Vision</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To transform mental health from a reactive, fragmented practice into a
                data-driven, computationally grounded discipline, using artificial
                intelligence as the primary engine of change.
                <br /><br />
                We believe mental health is not only a clinical issue, but a
                systems-level challenge that influences human potential, societal
                stability, and economic outcomes worldwide.
              </p>
            </div>

            <div className="bg-white/50 backdrop-blur-sm border border-gray-200 p-10 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-6 space-x-4">
                <div className="w-10 h-10 border border-gray-400 flex items-center justify-center text-sm font-light">02</div>
                <h3 className="text-2xl font-light">Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Our mission is to conduct foundational research at the intersection
                of artificial intelligence, cognitive science, and psychology.
                <br /><br />
                We develop and validate computational models that capture the
                dynamics of human thought processes, emotional regulation, and
                behavioral patterns — and translate them into systems that work in
                the real world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Leadership</span>
            <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6">Founder & team</h2>
            <div className="w-24 h-px bg-gray-900" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {PEOPLE.map((p) => (
              <div key={p.name} className="p-8 md:p-10 border border-gray-200" style={{ backgroundColor: '#fbfcf3' }}>
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gray-200 flex items-center justify-center text-2xl font-light text-gray-600">
                    {p.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="text-2xl font-light mb-1">{p.name}</h3>
                    <p className="text-sm text-gray-500 uppercase tracking-wider">{p.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">{p.bio}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-3xl border-t border-gray-300 pt-8">
            <p className="text-gray-700 leading-relaxed">
              We are hiring candidates from AI, ML, data science, software development,
              cognitive science and psychology backgrounds. If you are interested in
              joining the team, reach out — we read every message.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href="mailto:careers@cognitics.org" className="btn-ghost">careers@cognitics.org</a>
              <Link to="/contact" className="btn-ghost">Get in touch</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Collaborations */}
      <section className="py-24 md:py-32 px-6" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-4xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Partnerships</span>
          <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6">Collaborations</h2>
          <div className="w-20 h-px bg-gray-900 mb-8" />
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            We actively seek partnerships with universities, research institutions,
            and clinical organizations. If your work aligns with our research focus
            areas and you're interested in collaborative projects, please reach out.
          </p>
          <Link to="/contact" className="btn-primary">Start a conversation →</Link>
        </div>
      </section>
    </>
  );
}
